import { createSelector } from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';

import type { Project, TeamInvitation, User, Workspace, WorkspaceProject } from 'js/src/client';
import {
    DefaultDashboardViewEnum,
    RoleEnum,
    UserPermissions,
    VideoVisibility,
} from 'js/src/client';
import { getWorkspaceSlugFromPath } from 'js/src/data/account/utils';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import { getServerData } from 'js/src/libs/server-data';
import { type DeepReadonly, withStableEmptyArray } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import Cookies from 'js-cookie';

/* SELECTORS  */
type StoredWorkspace = DeepReadonly<Workspace>;
type StoredUser = DeepReadonly<User>;

const NO_WORKSPACE_IDS: readonly number[] = [];
const NO_WORKSPACES: readonly StoredWorkspace[] = [];
const NO_USERS: readonly StoredUser[] = [];
const NO_WORKSPACE_PROJECTS: readonly DeepReadonly<WorkspaceProject>[] = [];
const NO_PERMISSIONS: readonly UserPermissions[] = [];

export function isMemberInWorkspace(
    member: StoredUser | undefined,
    workspace: StoredWorkspace | undefined,
) {
    if (!member || !workspace) {
        return false;
    }
    return member.workspaces.includes(workspace.id);
}

const getCurrentTeamMember = (team_members: RootState['account']['team_members']) => {
    const userId = getServerData()?.USER?.id;
    return userId === undefined ? undefined : new PlainJsStoreORM(team_members).getById(userId);
};

export const getAccountWorkspaces = (state: RootState) => state.account.workspaces;

/**
 * selector for workspace IDs, and if filterByMember == true, then list of workspace IDs user is a member of
 * @param {StoreORM} state.account.workspaces
 * @param {boolean} filterByMember - flag to filter workspaceIds by whether user is a member of
 * @returns {number[]} - list of workspace IDs
 *
 * reselect input selectors receives arguments for all input selectors, so the 2nd input selector is just
 * for the argument to pass through to the transform function
 * https://redux.js.org/usage/deriving-data-selectors#createselector-behavior
 */
export const selectAllWorkspaceOrmIds = createSelector(
    (state: RootState) => state.account.workspaces,
    (state: RootState) => state.account.team_members,
    (state: RootState, filterByMember: boolean = true) => filterByMember, // argument pass-through
    (workspaces, team_members, filterByMember: boolean): readonly number[] => {
        const workspacesOrm = new PlainJsStoreORM(workspaces);

        let workspaceIds: readonly number[];
        if (filterByMember) {
            const user = getCurrentTeamMember(team_members);
            if (!user) {
                return NO_WORKSPACE_IDS;
            }
            workspaceIds = user.workspaces;
        } else {
            workspaceIds = workspacesOrm.allIds;
        }

        // Sort workspace IDs alphabetically by workspace name
        return withStableEmptyArray(
            [...workspaceIds].sort((a, b) => {
                const nameA = workspacesOrm.getById(a)?.name || '';
                const nameB = workspacesOrm.getById(b)?.name || '';
                return nameA.localeCompare(nameB);
            }),
        );
    },
);

export const getAllWorkspaceInfoList = createSelector(
    (state: RootState) => state.account.workspaces,
    (workspaces): readonly StoredWorkspace[] => {
        if (!workspaces) {
            return NO_WORKSPACES;
        }
        return withStableEmptyArray(
            new PlainJsStoreORM(workspaces).map((workspaceInfo) => workspaceInfo),
        );
    },
);

/**
 * selector to check if any workspaces have editor advanced settings enabled
 * @param {StoreORM} state.account.workspaces
 * @returns {Boolean} - True if any workspaces have editor advanced settings enabled
 */
export const checkAnyWorkspacesWithEditorAdvancedSettingsOn = createSelector(
    (state: RootState) => state.account.workspaces,
    (workspaces) => {
        if (!workspaces) {
            return false;
        }
        return new PlainJsStoreORM(workspaces).allIds.some(
            (id) => workspaces.byId[id].enable_editor_advanced_settings ?? false,
        );
    },
);

/**
 * selector to get workspaces where current user is a member of
 * @param {StoreORM} state.account.workspaces
 * @returns {Workspace[]} - returns workspace info where current user is a member of
 */
export const getMemberWorkspaceInfoList = createSelector(
    (state: RootState) => state.account.workspaces,
    (state: RootState) => state.account.team_members,
    (workspaces, team_members): readonly StoredWorkspace[] => {
        if (!team_members) {
            return NO_WORKSPACES;
        }
        const user = getCurrentTeamMember(team_members);
        if (!user) {
            return NO_WORKSPACES;
        }
        if (!workspaces) {
            return NO_WORKSPACES;
        }
        return withStableEmptyArray(
            new PlainJsStoreORM(workspaces).filter((workspaceInfo) =>
                user.workspaces.includes(workspaceInfo.id),
            ),
        );
    },
);

const NO_PROJECTS: readonly DeepReadonly<Project>[] = [];
const NO_PROJECT_IDS: readonly number[] = [];

export const getAllProjects = createSelector(
    (state: RootState) => state.account.projects,
    (projects): readonly DeepReadonly<Project>[] => {
        if (!projects) {
            return NO_PROJECTS;
        }
        return withStableEmptyArray(new PlainJsStoreORM(projects).map((project) => project));
    },
);

export const selectProjectsInWorkspace = createSelector(
    (state: RootState) => state.account.workspaces,
    (state: RootState, workspaceId: number | null) => workspaceId,
    (workspaces, workspaceId: number | null) => {
        if (workspaceId === null) {
            return NO_WORKSPACE_PROJECTS;
        }
        return (
            new PlainJsStoreORM(workspaces).getById(workspaceId)?.project_details ??
            NO_WORKSPACE_PROJECTS
        );
    },
);

export const selectProjectIdsInWorkspace = createSelector(
    (state: RootState) => state.account.projects,
    (state: RootState, workspaceId: number | null) => workspaceId,
    (projects, workspaceId: number | null) => {
        if (workspaceId === null) {
            return NO_PROJECT_IDS;
        }
        return withStableEmptyArray(
            new PlainJsStoreORM(projects)
                .filter((project) => project.workspace === workspaceId)
                .map((project) => project.id),
        );
    },
);

export function selectActiveWorkspace(state: RootState): StoredWorkspace | null {
    const workspaceId = getServerData()?.USER?.workspace_id;
    if (workspaceId) {
        const workspace = new PlainJsStoreORM(state.account.workspaces).getById(workspaceId);
        if (workspace) {
            return workspace;
        }
    }
    return null;
}

export function getActiveWorkspaceMembers(state: RootState): readonly StoredUser[] {
    const workspace = selectActiveWorkspace(state);

    if (!workspace) {
        return NO_USERS;
    }
    return selectAllTeamMembersInWorkspace(state, workspace.id);
}

export function selectActiveWorkspaceName(state: RootState): string {
    const workspace = selectActiveWorkspace(state);

    if (workspace) {
        /**
         * As user can manually change the workspace slug via the url,
         * SERVER.USER.workspace_id is not updated in that case.
         * Thus we check if the slug matches any logged-in user workspace slugs
         * and update the dropdown selection correctly.
         *  */
        const activeUserWorkspaceViaUrl = selectWorkspaceFromSlug(state, false);
        if (activeUserWorkspaceViaUrl && !isEqual(activeUserWorkspaceViaUrl, workspace)) {
            return activeUserWorkspaceViaUrl.name || '';
        } else {
            return workspace.name || '';
        }
    }

    return '';
}

export const getWorkspaceNameById = (workspaceId: number) => (state: RootState) => {
    const workspace = selectWorkspaceByOrmId(state, workspaceId);
    if (!workspace) return '';
    return getNameFromWorkspace(workspace);
};

export function getNameFromWorkspace(workspace: StoredWorkspace | undefined) {
    if (!workspace) {
        return '';
    }
    return workspace.name || '';
}

export function getWorkspaceEnabledEditorAdvancedSettingsById(
    state: RootState,
    workspaceId: number | null,
) {
    const workspace = selectWorkspaceByOrmId(state, workspaceId);
    if (!workspace) return null;
    return getAdvancedSettingsEnabledFromWorkspace(workspace);
}

export function getAdvancedSettingsEnabledFromWorkspace(workspace: StoredWorkspace | undefined) {
    if (!workspace) {
        return false;
    }
    return workspace.enable_editor_advanced_settings;
}

export function selectWorkspaceByOrmId(
    state: RootState,
    workspace_orm_id: number | null,
): StoredWorkspace | undefined {
    if (workspace_orm_id === null) {
        return undefined;
    }
    return new PlainJsStoreORM(state.account.workspaces).getById(workspace_orm_id);
}

export const getWorkspaceSlugById = (workspaceId: number) => (state: RootState) => {
    const workspace = selectWorkspaceByOrmId(state, workspaceId);
    return workspace?.slug || '';
};

export const getWorkspaceFeedSecret = (workspaceId: number) => (state: RootState) => {
    const workspace = selectWorkspaceByOrmId(state, workspaceId);
    return workspace?.feed_secret || '';
};

const NO_WORKSPACE_NAMES: Readonly<Record<number, string>> = {};
const NO_WORKSPACE_ADVANCED_SETTINGS: Readonly<Record<number, boolean | null>> = {};

export const getNamesOfWorkspacesUserHasAccessTo = createSelector(
    (state: RootState) => state.account.workspaces,
    (state: RootState, teamMember: StoredUser | null) => teamMember,
    (workspaces, teamMember): Readonly<Record<number, string>> => {
        if (!teamMember) {
            return NO_WORKSPACE_NAMES;
        }
        const workspacesOrm = new PlainJsStoreORM(workspaces);
        return Object.fromEntries(
            teamMember.workspaces.map((workspaceId) => [
                workspaceId,
                getNameFromWorkspace(workspacesOrm.getById(workspaceId)),
            ]),
        );
    },
);

export const checkAdvancedSettingsEnabledForWorkspacesUserHasAccessTo = createSelector(
    (state: RootState) => state.account.workspaces,
    (state: RootState, teamMember: StoredUser | null) => teamMember,
    (workspaces, teamMember): Readonly<Record<number, boolean | null>> => {
        if (!teamMember) {
            return NO_WORKSPACE_ADVANCED_SETTINGS;
        }
        const workspacesOrm = new PlainJsStoreORM(workspaces);
        return Object.fromEntries(
            teamMember.workspaces.map((workspaceId) => [
                workspaceId,
                getAdvancedSettingsEnabledFromWorkspace(workspacesOrm.getById(workspaceId)),
            ]),
        );
    },
);

/**
 * selector to get default visibility of a workspace
 * @param {state} - state to select state.account.workspaces
 * @param {workspaceId} - id of workspace we want to get default_visibility out of
 * @returns {string} - one of VideoVisibility
 */
export const selectDefaultWorkspaceVisibilityByWorkspaceId = createSelector(
    (state: RootState) => state.account.workspaces, // direct lookup for workspaces
    (state: RootState, workspaceId: number) => workspaceId, // argument pass-through for transform function
    (workspaces, workspaceId: number) => {
        const workspace = new PlainJsStoreORM(workspaces).getByIdOrThrow(workspaceId);
        return workspace.default_visibility || VideoVisibility.UNLISTED;
    },
);

export function selectWorkspaceFromSlug(state: RootState, navigationRequired = true) {
    const slug = getWorkspaceSlugFromPath(navigationRequired);
    return new PlainJsStoreORM(state.account.workspaces).find(
        (workspace) => workspace.slug === slug,
    );
}

export function selectWorkspaceByWorkspaceId(state: RootState, id: number) {
    return new PlainJsStoreORM(state.account.workspaces).getByIdOrThrow(id);
}

export function selectAllTeamMemberOrmIds(state: RootState): readonly number[] {
    return withStableEmptyArray(state.account.team_members.allIds);
}

const selectTeamMembersInWorkspaceByMembership = createSelector(
    (state: RootState) => state.account.team_members,
    (state: RootState, workspaceId: number) => workspaceId,
    (team_members, workspaceId): readonly StoredUser[] =>
        withStableEmptyArray(
            new PlainJsStoreORM(team_members).filter((teamMember) =>
                teamMember.workspaces.includes(workspaceId),
            ),
        ),
);

export function selectAllTeamMembersInWorkspace(state: RootState, workspaceId: number) {
    return selectTeamMembersInWorkspaceByMembership(state, workspaceId);
}

const selectAdminTeamMembers = createSelector(
    (state: RootState) => state.account.team_members,
    (team_members): readonly StoredUser[] | null => {
        const orm = new PlainJsStoreORM(team_members);
        if (orm.isEmpty()) {
            // haven't loaded yet
            return null;
        }
        return withStableEmptyArray(orm.filter((member) => member.role === RoleEnum.ADMIN));
    },
);

export function selectAllAdminTeamMembers(state: RootState): readonly StoredUser[] | null {
    return selectAdminTeamMembers(state);
}

export function selectTeamMemberByOrmId(state: RootState, team_member_orm_id: number) {
    return new PlainJsStoreORM(state.account.team_members).getByIdOrThrow(team_member_orm_id);
}

export const getAllTeamMembers = createSelector(
    (state: RootState) => state.account.team_members,
    (team_members): readonly StoredUser[] =>
        withStableEmptyArray(new PlainJsStoreORM(team_members).map((teamMember) => teamMember)),
);

/**
 * @param {RootState} state - rootState
 * @returns int of team size
 */
export const getTeamSize = (state: RootState) => {
    return state.account.team_members.allIds.length;
};

/**
 * selects all team members that belong to a specific workspace
 * @param {RootState} state - root state to look-up workspaces and team_members states
 * @param {string} workspaceId - id of specific workspace you want team members of
 * @returns {User[]} - list of team members within this specific workspace
 */
export const selectTeamMembersInWorkspace = createSelector(
    (state: RootState) => state.account.workspaces, // direct lookup to workspaces
    (state: RootState) => state.account.team_members, // direct lookup to team
    (state: RootState, workspaceId: number) => workspaceId, // argument passthrough to transform function
    (workspaces, team, workspaceId: number): readonly StoredUser[] => {
        const workspace = new PlainJsStoreORM(workspaces).getById(workspaceId);
        if (!workspace) {
            return NO_USERS;
        }
        return withStableEmptyArray(
            new PlainJsStoreORM(team).filter((member) => isMemberInWorkspace(member, workspace)),
        );
    },
);

/**
 * selects users that are on the same team but not within the specified workspace
 * @param {RootState} state - root state to look-up workspaces and team_members states
 * @param {string} workspaceId - id of specific workspace
 * @returns {User[]} - list of team members not in this workspace
 */
export const selectTeamMembersNotInWorkspace = createSelector(
    (state: RootState) => state.account.workspaces,
    (state: RootState) => state.account.team_members,
    (state: RootState, workspaceId: number) => workspaceId,
    (workspaces, team, workspaceId: number): readonly StoredUser[] => {
        const workspace = new PlainJsStoreORM(workspaces).getByIdOrThrow(workspaceId);
        return withStableEmptyArray(
            new PlainJsStoreORM(team).filter(
                (member) =>
                    member.id !== getServerData()?.USER?.id &&
                    !isMemberInWorkspace(member, workspace),
            ),
        );
    },
);

export const hasUserWithOnlyThisWorkspace = createSelector(
    (state: RootState) => state.account.team_members,
    (state: RootState, workspaceId: number) => workspaceId,
    (team, workspaceId: number) => {
        return (
            new PlainJsStoreORM(team).filter((member) => {
                const workspaces = member.workspaces;
                return workspaces && workspaces.includes(workspaceId) && workspaces.length === 1;
            }).length > 0
        );
    },
);

export const selectUsersWithAccessToWorkspaceAndProject = createSelector(
    (state: RootState) => state.account.team_members,
    (state: RootState) => state.account.workspaces,
    (state: RootState, workspaceId: number | undefined) => workspaceId,
    (state: RootState, workspaceId: number | undefined, projectId: number | null) => projectId,
    (state: RootState) => state.account.projects,
    (allTeamMembers, workspaces, workspaceId, projectId, projects): readonly StoredUser[] => {
        if (workspaceId === undefined) {
            return NO_USERS;
        }
        // ADMINS
        const allTeamMembersOrm = new PlainJsStoreORM(allTeamMembers);
        if (allTeamMembersOrm.isEmpty()) {
            return NO_USERS;
        }
        if (!new PlainJsStoreORM(workspaces).contains(workspaceId)) {
            return NO_USERS;
        }

        // access = workspace admins + project users
        const admins = allTeamMembersOrm.filter((member) => {
            return member.workspaces.includes(workspaceId) && member.role === RoleEnum.ADMIN;
        });

        // PROJECT USERS
        const projectUsers =
            projectId !== null && new PlainJsStoreORM(projects).contains(projectId)
                ? allTeamMembersOrm.filter((member) => member.projects.includes(projectId))
                : [];
        return withStableEmptyArray([...new Set([...admins, ...projectUsers])]);
    },
);

export function selectWorkspaceTeamSize(state: RootState, workspaceId: number) {
    return selectTeamMembersInWorkspace(state, workspaceId).length;
}

export function selectUserProjects(state: RootState) {
    return state.account?.user_profile?.user_projects;
}

export function getUserId(state: RootState) {
    return state.account.user_profile.user_id;
}

export function getFirstName(state: RootState) {
    return state.account.user_profile.first_name;
}

export function getLastName(state: RootState) {
    return state.account.user_profile.last_name;
}

export function selectDashboardName(state: RootState) {
    return state.account.user_profile.first_name || state.account.user_profile.email;
}

export function canSeeVideoControls(state: RootState) {
    // Backend-authoritative: current_user_can_download is the DownloadVideo scoped-access rule
    // plus a VIEWER exclusion -- showControls gates writes (Edit, rename, visibility) too, so a
    // read-only VIEWER is excluded here while keeping their DownloadVideo.get download.
    return Boolean(state.shareVideo.video?.current_user_can_download);
}

export function getProjectsLoadingIndicator(state: RootState) {
    return state.account.projects_loading_indicator;
}

export function select2FAActivated(state: RootState) {
    return state.account.user_profile.two_factor_activated;
}

export function getTeamMFASettingFlag(state: RootState) {
    return state.account.team_settings.mfa_required;
}

export function getDateJoinedCycleStartDate(state: RootState) {
    return state.account.team_settings.date_joined_cycle_start_date;
}

export function getDateJoinedCycleEndDate(state: RootState) {
    return state.account.team_settings.date_joined_cycle_end_date;
}

export function getVideosCreatedInCycle(state: RootState) {
    return state.account.team_settings.videos_created_in_cycle;
}

export function getMaxVideosInCycle(state: RootState) {
    return state.account.team_settings.max_videos_in_cycle;
}

export function getMaxVideoDurationSeconds(state: RootState) {
    return state.account.team_settings.max_video_duration_seconds;
}

export function getCanUseAIVoiceover(state: RootState): boolean {
    return state.account.team_settings.can_use_ai_voiceover_flow;
}

export function getCanUseAIScript(state: RootState) {
    return state.account.team_settings.can_use_ai_script_flow;
}

export function getCanUseOutlineAnIdea(state: RootState): boolean {
    return state.account.team_settings.can_use_outline_an_idea;
}

export function getCanUsePremiumAIVoices(state: RootState): boolean {
    return state.account.team_settings.can_use_premium_ai_voices;
}

export function getCanUseEditorialMedia(state: RootState): boolean {
    return state.account.team_settings.can_use_editorial_media;
}

export function getCanUseShutterstockUnlimitedMedia(state: RootState): boolean {
    return state.account.team_settings.can_use_shutterstock_unlimited_media;
}

export function getCanUseAIAvatar(state: RootState): boolean {
    return state.account.team_settings.can_use_ai_avatar;
}

export function getCanPublishAIAvatar(state: RootState): boolean {
    return state.account.team_settings.can_publish_ai_avatar;
}

export function getCanUseAIMedia(state: RootState): boolean {
    return state.account.team_settings.can_use_ai_media;
}

export function getCanUseAnimatedExplainer(state: RootState): boolean {
    return state.account.team_settings.can_use_animated_explainer;
}

// team_settings is fetched asynchronously, so its fields hold reducer defaults until the
// fetch resolves. date_joined_cycle_start_date is always populated by storeTeamSettings and
// is null until then, making it a reliable "settings have loaded" signal. Consumers that act
// on a capability flag (e.g. redirecting on !getCanUseGenX) must wait for this to avoid acting
// on default values before the real ones arrive.
export function getTeamSettingsLoaded(state: RootState): boolean {
    return state.account.team_settings.date_joined_cycle_start_date !== null;
}

// GenX (animated explainers) relies on AI voiceover, script generation, and AI media, and can
// also be disabled outright for a team via can_use_animated_explainer.
// A team that has opted out of any one of these cannot use GenX.
//
// Returns null until team settings have loaded — the capability flags hold reducer defaults
// before then, so distinguishing "not yet known" from a real false matters. Callers must
// branch on `=== false` (definitely blocked) vs `=== true` (definitely allowed) rather than
// truthiness, so they never act on the unknown state (e.g. redirecting before the fetch).
export function getCanUseGenX(state: RootState): boolean | null {
    if (!getTeamSettingsLoaded(state)) return null;
    return Boolean(
        getCanUseAIVoiceover(state) &&
        getCanUseAIScript(state) &&
        getCanUseAIMedia(state) &&
        getCanUseAnimatedExplainer(state),
    );
}

export function getCanUseSketch(state: RootState): boolean {
    return state.account.team_settings.can_use_sketch;
}

export function getCanUseScreenRecording(state: RootState): boolean {
    return state.account.team_settings.can_use_screen_recording;
}

export function isVideoLimitExceededInCycle(state: RootState) {
    const videosCreated = getVideosCreatedInCycle(state);
    const videosAllowed = getMaxVideosInCycle(state);
    if (typeof videosCreated !== 'number' || typeof videosAllowed !== 'number') {
        // the limit is null or undefined, so there is no limit!
        return false;
    }
    return videosCreated >= videosAllowed;
}

export function getTeamVideoApprovalFlag(state: RootState): boolean {
    return state.account.team_settings.can_request_video_approval;
}

export function getTeamHasAuditLog(state: RootState): boolean {
    return state.account.team_settings.has_audit_log;
}

export function isApprovalWorkflowRestricted(state: RootState): boolean | null {
    const activeWorkspace = selectActiveWorkspace(state);
    if (activeWorkspace) {
        return (
            activeWorkspace.editors_require_approval_to_publish === true &&
            getTeamVideoApprovalFlag(state)
        );
    } else {
        return null;
    }
}

export function getUserPermissions(state: RootState, userId: number): readonly UserPermissions[] {
    if (getServerData()?.USER?.id === userId) {
        return state.account.user_profile.permissions;
    }

    const teamMember = new PlainJsStoreORM(state.account.team_members).getById(userId);
    return teamMember?.permissions ?? NO_PERMISSIONS;
}

export const getIsUserAllowedAccessToAccessBlueprintsAndBrandKits = (state: RootState): boolean => {
    /**
     * Return a boolean indicating if the user has access to blueprints and brand kits for the active workspace.
     * Because this controls visibility of tabs in the dashboard sidebar and depends on the workspace loading state,
     * we cache the value in a cookie to prevent flickering of the sidebar tabs while the workspace is loading.
     */
    const activeWorkspace = selectActiveWorkspace(state);

    const cookieVal = Cookies.get('access_saved_templates_and_brand_kits');
    if (!activeWorkspace) {
        // we haven't loaded workspaces yet, so unable to determine if user has access
        // fallback to cached value in Cookies (or default to false)
        return cookieVal === 'true';
    }

    let isAllowed = false;
    const isAdmin = getServerData()?.USER?.role === RoleEnum.ADMIN;
    if (isAdmin) {
        isAllowed = true;
    } else if (!activeWorkspace.enable_editor_advanced_settings) {
        isAllowed = false;
    } else {
        const permissions = state.account.user_profile.permissions;
        isAllowed = permissions.includes(UserPermissions.ACCESS_SAVED_TEMPLATES_AND_BRAND_KITS);
    }
    // cache the value in a cookie
    if (cookieVal !== isAllowed.toString()) {
        Cookies.set('access_saved_templates_and_brand_kits', isAllowed.toString());
    }
    return isAllowed;
};

const isUserAllowedToUseAdvancedPermission = (
    state: RootState,
    acceptablePermissions: UserPermissions[],
) => {
    if (getServerData()?.USER?.role === RoleEnum.ADMIN) {
        return true;
    }

    const activeWorkspace = selectActiveWorkspace(state);
    if (activeWorkspace) {
        if (!activeWorkspace.enable_editor_advanced_settings) {
            return false;
        }

        const permissions = state.account.user_profile.permissions;
        return acceptablePermissions.some((permission) => permissions.includes(permission));
    }
    return false;
};

export const getIsUserAllowedToManageProject = (state: RootState) => {
    return isUserAllowedToUseAdvancedPermission(state, [UserPermissions.CREATE_AND_MANAGE_PROJECT]);
};

export const getIsUserAllowedAccessToFonts = (state: RootState) => {
    return isUserAllowedToUseAdvancedPermission(state, [
        UserPermissions.ACCESS_SAVED_TEMPLATES_AND_BRAND_KITS,
        UserPermissions.ACCESS_FONTS,
    ]);
};

export const getIsUserAllowedAccessToWatermarks = (state: RootState) => {
    return isUserAllowedToUseAdvancedPermission(state, [
        UserPermissions.ACCESS_SAVED_TEMPLATES_AND_BRAND_KITS,
        UserPermissions.ACCESS_WATERMARKS,
    ]);
};

export const getIsUserAllowedAccessToColorPalettes = (state: RootState) => {
    return isUserAllowedToUseAdvancedPermission(state, [
        UserPermissions.ACCESS_SAVED_TEMPLATES_AND_BRAND_KITS,
        UserPermissions.ACCESS_COLOUR_PALETTES,
    ]);
};

export const getIsUserAllowedAccessToMoveAndResizeText = (state: RootState) => {
    return isUserAllowedToUseAdvancedPermission(state, [
        UserPermissions.ACCESS_MOVE_AND_RESIZE_TEXT,
    ]);
};

export const getIsUserAllowedAccessToMoveAndResizeIcons = (state: RootState) => {
    return isUserAllowedToUseAdvancedPermission(state, [
        UserPermissions.ACCESS_MOVE_AND_RESIZE_ICONS,
    ]);
};

export const getIsUserAllowedAccessToMoveAndResizeCaptions = (state: RootState) => {
    return isUserAllowedToUseAdvancedPermission(state, [
        UserPermissions.ACCESS_MOVE_AND_RESIZE_CAPTIONS,
    ]);
};

/** Team size selectors */

export function getTeamPurchasedSeats(state: RootState) {
    return state.account.team_settings.purchased_seats;
}

export function getTeamPurchasedSeatCeiling(state: RootState) {
    return state.account.team_settings.purchased_seat_ceiling;
}

export function getTeamMaxTeamSize(state: RootState) {
    return state.account.team_settings.max_team_size;
}

export function getCanTranslateVideos(state: RootState) {
    return state.account.team_settings.can_translate_videos;
}

// Is self serve and has no seats left
export function getShouldRequireSeatPurchase(state: RootState) {
    const isEnterprise = getServerData()?.USER?.is_enterprise_plan;
    const hasRemainingSeats =
        getTeamSize(state) + getPendingInvitationCount(state) < getTeamMaxTeamSize(state);
    return !isEnterprise && !hasRemainingSeats;
}

// Is enteprise and has no seats left
export function enterpriseNoOpenSeats(state: RootState) {
    const isEnterprise = getServerData()?.USER?.is_enterprise_plan;
    const hasRemainingSeats =
        getTeamSize(state) + getPendingInvitationCount(state) < getTeamMaxTeamSize(state);
    return isEnterprise && !getServerData()?.USER?.unlimited_invitations && !hasRemainingSeats;
}

/**
 * Invitations
 */

export function selectPendingInvitationIds(state: RootState): readonly number[] {
    return withStableEmptyArray(state.account.invitations.allIds);
}

export function selectPendingInvitationById(
    state: RootState,
    id: number,
): DeepReadonly<TeamInvitation> | undefined {
    return new PlainJsStoreORM(state.account.invitations).getById(id);
}

export function getPendingInvitationCount(state: RootState) {
    return state.account.invitations.allIds.length;
}

export function isInvitationsLoading(state: RootState) {
    return state.account.invitationsIsLoading;
}

/** Account selectors */

export function getAccountEmail(state: RootState) {
    return state.account.user_profile.email;
}

export function getAccountLoadingStatus(state: RootState) {
    return state.account.user_profile.loading;
}

export function getAccountPhotoUrl(state: RootState) {
    return state.account.user_profile.photo_url;
}

export function getIsUserStaff(state: RootState) {
    return state.account.user_profile.is_staff;
}

/**
 * SSO selectors
 */

export function getCanSeeSSOSettings(state: RootState) {
    return state.account.team_settings.can_see_sso_settings;
}

export function getSSOIdPConfig(state: RootState) {
    return state.account.team_settings.sso_idp_config;
}

export function getSSOLoginStatus(state: RootState) {
    return state.account.team_settings.sso_login_status;
}

export function getAutoBlueprintRecommendationsEnabled(state: RootState): boolean {
    return state.account.team_settings.auto_blueprint_recommendations_enabled ?? true;
}

export function getDefaultDashboardView(state: RootState) {
    return state.account.team_settings.default_dashboard_view ?? DefaultDashboardViewEnum.CREATE;
}

export function getGenxPresetRestrictionEnabled(state: RootState): boolean {
    return state.account.team_settings.genx_preset_restriction_enabled ?? false;
}

export function getAllowedGenxPresetSlugs(state: RootState): string[] {
    return state.account.team_settings.allowed_genx_preset_slugs;
}
